import React, {createContext, useContext, useReducer} from 'react'

const LOCAL_STORAGE_KEY = 'ed-it-warehouse-user-state'

interface UserState{
  token: string
}

let initialState: UserState = {
  token: ''
}

if(localStorage.getItem(LOCAL_STORAGE_KEY)){
  initialState = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)!)
}

if(initialState.token === ''){
  const query = window.location.search
  const params = new URLSearchParams(query)
  
  if(params.has('token')){
    initialState.token = params.get('token')!
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(initialState))
  }
}

const UserStateContext = createContext(initialState)
const UserDispatchContext = createContext<React.Dispatch<UserActions> | undefined>(undefined)

type UserActions = 
  ReducerAction<"login", UserState> |
  ReducerAction<"logout", undefined>

const userReducer = (state: UserState, action: UserActions) => {
  switch(action.type){
    case 'login':
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(action.payload))
      return Object.assign({}, action.payload)
    case 'logout':
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      return Object.assign({}, initialState)
  }
}

export const UserContext: React.FC = ({children}) => {
  const [state, dispatch] = useReducer(userReducer, initialState)

  return <UserStateContext.Provider value={state}>
    <UserDispatchContext.Provider value={dispatch}>
      {children}
    </UserDispatchContext.Provider>
  </UserStateContext.Provider>
}

export const useUser = () => {
  const user = useContext(UserStateContext)

  if(!user){
    throw new Error("`useUser` can only be called inside a <UserContext>")
  }

  return user
}

export const useUserActions = () => {
  const dispatch = useContext(UserDispatchContext)

  if(!dispatch){
    throw new Error("`useUserActions` can only be called inside a <UserContext>")
  }

  return {
    login: (user: UserState) => {
      dispatch({type: 'login', payload: user})
    },
    logout: () => {
      dispatch({type: 'logout', payload: undefined})
    }
  }
}